import { Route, Routes, useLocation, Navigate } from 'react-router';
import { Suspense, lazy, useEffect, useState } from 'react';
import Loader from './components/Loader/Loader';
import { useAppContext } from './context/AppContext.jsx';
import StillHaveQuestions from './components/StillHaveQuestions/StillHaveQuestions.jsx';
import HeaderLoader from './components/ShimmerLoaders/header/HeaderLoader.jsx';
import setWindowObject from './utils/chatbot/setWindowObject.jsx';
import ChatLanding from './pages/ChatLanding/ChatLanding.jsx';
import { useCloseChatBot, useShowBlankPage } from './hooks/common.jsx';
import usePageLoadTracking, { useFetchHeaderFooter, useReferrer } from './hooks/AppHooks.jsx';
import { useIsWrapper } from './hooks/HomeHooks.jsx';
import { logNewRelicError } from './utils/newRelicApiLogger.js';
const Chat = lazy(() => import('./components/Chat/Chat.jsx'));
const ArticleSearchResults = lazy(
    () => import('./pages/ArticleSearchResults/ArticleSearchResults.jsx'),
);
const HardShipMain = lazy(() => import('./pages/HardshipForm/HardShipMain'));
const ProtectedRoute = lazy(() => import('./components/protectedRoutes/ProtectedRoute.jsx'));
const RaiseaComplaint = lazy(() => import('./pages/RaiseaComplaint/RaiseaComplaint.jsx'));
const DetachedArticle = lazy(() => import('./pages/DetachedArticle/DetachedArticle.jsx'));
const CategoryPage = lazy(() => import('./pages/CategoryPage/CategoryPage.jsx'));
const Header = lazy(() => import('./components/Header/Header'));
const Footer = lazy(() => import('./components/Footer/Footer'));
const Home = lazy(() => import('./pages/Home/Home'));
const SubSection = lazy(() => import('./pages/SubSection/SubSection'));
const Journey = lazy(() => import('./pages/journey/Journey'));
const EnquiryForm = lazy(() => import('./pages/EnquiryForm/EnquiryForm.jsx'));

const App = () => {
    useCloseChatBot();
    usePageLoadTracking();
    const { headerContent, footerContent, isFoxtelContentReady } = useFetchHeaderFooter();
    const [showChatBot, setShowChatBot] = useState(false);
    const { userData, setUserData } = useAppContext();
    const showBlankPage = useShowBlankPage();
    const location = useLocation();
    const { isWrapper } = useIsWrapper();
    const referrer = useReferrer();

    // initiate new relic tracking after app mount
    // to avoid this fromn going into first bundle file since this is heavy
    useEffect(() => {
        import('./utils/newRelicVendorScript.js')
            .then(({ setUpNewRelic }) => {
                setTimeout(() => setUpNewRelic(), 0);
            })
            .catch((e) => {
                logNewRelicError({
                    errorName: 'new relic setup',
                    attributes: {
                        message: e?.message
                    }
                })
            });
    }, []);

    useEffect(() => {
        userData !== null && setShowChatBot(true);
    }, [userData]);

    // TODO (SM): enable this is foxtel header regularly fails to update user logged in status
    useEffect(() => {
        if (referrer.startsWith('https://www.foxtel.com.au/login.html')) {
            // user came from foxtel login page
            // window.location.reload()
        }
    }, [referrer]);

    // Check auth ONCE on app mount
    useEffect(() => {
        async function checkAuth() {
            try {
                const { fetchUserData } = await import('./services/dataResolver/dataResolver');
                const { success: isUserDataFetchSuccess, data: userDataRes } =
                    await fetchUserData();

                // if api fails, set empty data for user.
                // because, for non-logged-in users, api response is empty object
                if (!isUserDataFetchSuccess) {
                    console.log('Auth Fail: ', userDataRes);
                    setUserData({});
                } else {
                    setUserData(userDataRes);
                }

                if (userDataRes) {
                    setWindowObject({
                        userName: userDataRes?.firstName,
                        email: userDataRes?.email,
                        accountId: userDataRes?.accountNumber,
                    });
                }
            } catch (error) {
                console.error('Auth check failed:', error);
                setUserData(null);
            }
        }

        checkAuth();
    }, [location.pathname]);

    useEffect(() => {
        // version id
        const id = '0a657b6a-e119-46e3-80c0-d0298aabf29a';
        console.info('%cv:%c ' + id, 'color: gray;', 'color: #4abdff;');
    }, []);

    return (
        <div>
            {/* ========= header ========= */}
            {!showBlankPage && (
                <Suspense fallback={<HeaderLoader />}>
                    <Header
                        headerContent={headerContent}
                        isFoxtelContentReady={isFoxtelContentReady}
                    />
                </Suspense>
            )}

            {/* ========= page content ========= */}
            <Suspense fallback={<Loader />}>
                <Routes>
                    <Route index element={<Home />} />
                    <Route
                        path={`/sub-section/:subSectionLevel1Id/:subSectionLevel2Id`}
                        element={<SubSection />}
                    />
                    <Route
                        path={`/sub-section/:subSectionLevel1Id/:subSectionLevel2Id/:subSectionLevel2Name`}
                        element={<SubSection />}
                    />
                    <Route
                        path={`/category/:categoryId/:categoryName`}
                        element={<CategoryPage />}
                    />
                    <Route path={`/troubleshooting/:journeyId`} element={<Journey />} />
                    <Route
                        path={`/troubleshooting/:journeyId/journey/:journeyHistory`}
                        element={<Journey />}
                    />
                    <Route
                        path={`/troubleshooting/:journeyId/journey/:journeyHistory/article/:articleNumber`}
                        element={<Journey />}
                    />
                    <Route
                        path="/hardship-form"
                        element={
                            <ProtectedRoute>
                                <HardShipMain />
                            </ProtectedRoute>
                        }
                    />
                    <Route path={`/complaint`} element={<RaiseaComplaint />} />
                    <Route path={`/enquiry`} element={<EnquiryForm />} />
                    <Route path={`/article/:articleId`} element={<DetachedArticle />} />
                    <Route path={`/search-results`} element={<ArticleSearchResults />} />
                    <Route path={`/chat-landing`} element={<ChatLanding />} />

                    {/* ======= invalid routes ======== */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Suspense>

            {!showBlankPage && (
                <>
                    {/* ========= Common bottom part for all pages ========= */}
                    <StillHaveQuestions />

                    {/* ========= Footer ========= */}
                    <Footer
                        content={footerContent}
                        // showFoxtelSentContent={!isWrapper}
                        isWrapper={isWrapper}
                    />
                </>
            )}

            {/* ========= Chat bot ========= */}
            {showChatBot && (
                <Suspense>
                    <Chat disableAutoOpen={!showBlankPage} />
                </Suspense>
            )}
        </div>
    );
};

export default App;
